/**
 * VersionBanner.jsx
 * Detects when a newer build has been deployed while this tab is still
 * open and shows a slim "New version available" banner with a Refresh
 * button — instead of leaving a stale bundle running until something in it
 * breaks (e.g. after a deploy, an in-memory app can be calling an endpoint
 * or reading a shape of data that no longer matches the current backend).
 *
 * Mounted once, outside <App/>, so it works no matter what screen the app
 * itself is showing (including its own loading/auth-gate states).
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

async function fetchBuildId() {
  const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) return null;
  const data = await res.json();
  return data?.buildId || null;
}

export default function VersionBanner() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const loadedBuildId = useRef(null);
  const checking = useRef(false);

  const check = useCallback(async () => {
    if (checking.current) return;
    checking.current = true;
    try {
      const current = await fetchBuildId();
      if (!current) return;
      if (loadedBuildId.current === null) {
        // First successful read — this is the build this tab is running.
        loadedBuildId.current = current;
      } else if (current !== loadedBuildId.current) {
        setUpdateAvailable(true);
      }
    } catch {
      // Offline or backend hiccup — not worth surfacing, just try again later.
    } finally {
      checking.current = false;
    }
  }, []);

  useEffect(() => {
    check();
    const interval = setInterval(check, CHECK_INTERVAL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [check]);

  if (!updateAvailable) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 20000,
      background: '#0ea5e9', color: '#04121f',
      padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14,
      fontFamily: "'Inter', system-ui, sans-serif", fontSize: 13.5, fontWeight: 600,
      boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
    }}>
      <span>🚀 A new version of ZenAnalytics is available.</span>
      <button
        onClick={() => window.location.reload()}
        style={{
          background: '#04121f', color: '#e0f2fe', border: 'none', borderRadius: 20,
          padding: '5px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        Refresh →
      </button>
    </div>
  );
}
